import { api } from "@/services/auth";
import {
  createEmptyWorkspaceTabsByModule,
  getWorkspaceModuleIdForPath,
  migrateStoredTabs,
  normalizeWorkspaceRoutePath,
  type WorkspaceActiveTabByModule,
  type WorkspaceModuleId,
  type WorkspaceTabRecord,
  type WorkspaceTabsByModule,
} from "@/contexts/workspaceState";
import type { ModuleKey } from "@/modules/workspace/moduleManifests";

type EmptyStateReason = "unauthorized" | "forbidden" | "empty";

type WithEmptyState = {
  emptyStateReason?: EmptyStateReason;
};

export type WorkspaceTabDTO = Omit<WorkspaceTabRecord, "icon">;

const WORKSPACE_MODULE_IDS: WorkspaceModuleId[] = [
  "geral",
  "comercial",
  "marcas",
  "patentes",
  "di",
  "software",
  "autoral",
  "operacional",
  "internacional",
  "admin",
];

type LegacyWorkspaceTabsStateDTO = {
  tabs: WorkspaceTabDTO[];
  activeTabId: string | null;
};

type WorkspaceTabsStateDTO = {
  tabsByModule: WorkspaceTabsByModuleDTO;
  activeTabIdByModule: WorkspaceActiveTabByModule;
};

type WorkspaceTabsByModuleDTO = Record<WorkspaceModuleId, WorkspaceTabDTO[]>;

export type SaveWorkspaceTabsPayload = {
  tabsByModule: WorkspaceTabsByModule;
  activeTabIdByModule: WorkspaceActiveTabByModule;
};

function toWorkspaceTabRecord(tab: WorkspaceTabDTO): WorkspaceTabRecord {
  return { ...tab };
}

function toWorkspaceTabDTO(tab: WorkspaceTabRecord): WorkspaceTabDTO {
  const { id, path, label, pinned, kind, restorationKey, updatedAt } = tab;
  return { id, path, label, pinned, kind, restorationKey, updatedAt };
}

function migrateLegacyWorkspaceTabsState(
  tabs: WorkspaceTabDTO[],
  activeTabId: string | null,
): SaveWorkspaceTabsPayload {
  const tabsByModule = createEmptyWorkspaceTabsByModule();
  const activeTabIdByModule: WorkspaceActiveTabByModule = {};
  const normalizedActiveTabId = activeTabId ? normalizeWorkspaceRoutePath(activeTabId) : null;
  const seenTabIds = new Set<string>(tabsByModule.geral.map((tab) => tab.id));

  for (const tab of migrateStoredTabs(tabs)) {
    const moduleId = getWorkspaceModuleIdForPath(tab.path);
    if (seenTabIds.has(tab.id)) {
      continue;
    }

    seenTabIds.add(tab.id);
    tabsByModule[moduleId] = [...tabsByModule[moduleId], toWorkspaceTabRecord(tab)];

    if (tab.id === normalizedActiveTabId) {
      activeTabIdByModule[moduleId] = tab.id;
    }
  }

  for (const moduleId of WORKSPACE_MODULE_IDS) {
    if (!activeTabIdByModule[moduleId]) {
      activeTabIdByModule[moduleId] = tabsByModule[moduleId][0]?.id ?? null;
    }
  }

  return { tabsByModule, activeTabIdByModule };
}

function normalizeWorkspaceTabsState(
  data: WorkspaceTabsStateDTO | LegacyWorkspaceTabsStateDTO | null,
): SaveWorkspaceTabsPayload | null {
  if (!data) {
    return null;
  }

  if ("tabs" in data) {
    return migrateLegacyWorkspaceTabsState(data.tabs, data.activeTabId);
  }

  const tabsByModule = createEmptyWorkspaceTabsByModule();
  const activeTabIdByModule: WorkspaceActiveTabByModule = {};
  const seenTabIds = new Set<string>(tabsByModule.geral.map((tab) => tab.id));

  for (const moduleId of WORKSPACE_MODULE_IDS) {
    const moduleTabs = Array.isArray(data.tabsByModule?.[moduleId]) ? data.tabsByModule[moduleId] : [];
    const sanitizedTabs = moduleTabs.length > 0 ? migrateStoredTabs(moduleTabs) : [];

    for (const tab of sanitizedTabs) {
      const ownerModuleId = getWorkspaceModuleIdForPath(tab.path);
      if (seenTabIds.has(tab.id)) {
        continue;
      }

      seenTabIds.add(tab.id);
      tabsByModule[ownerModuleId] = [...tabsByModule[ownerModuleId], toWorkspaceTabRecord(tab)];
    }
  }

  for (const moduleId of WORKSPACE_MODULE_IDS) {
    const normalizedActiveTabId =
      typeof data.activeTabIdByModule?.[moduleId] === "string"
        ? normalizeWorkspaceRoutePath(data.activeTabIdByModule[moduleId] as string)
        : null;

    activeTabIdByModule[moduleId] =
      normalizedActiveTabId && tabsByModule[moduleId].some((tab) => tab.id === normalizedActiveTabId)
        ? normalizedActiveTabId
        : tabsByModule[moduleId][0]?.id ?? null;
  }

  return { tabsByModule, activeTabIdByModule };
}

export type ModuleUiStateDTO = WithEmptyState & {
  moduleKey: ModuleKey;
  sidebarCollapsed: boolean;
  aiHidden: boolean;
  updatedAt?: string | null;
};

export type ModuleFavoriteDTO = {
  id: string;
  favoriteType: string;
  entityId?: string | null;
  route: string;
  label: string;
  iconKey?: string | null;
  sortOrder: number;
  metadata?: Record<string, unknown>;
  updatedAt?: string | null;
};

export type ModuleMetricDTO = {
  key: string;
  label: string;
  value: number;
  helper?: string | null;
  tone?: string;
};

export type ModuleListItemDTO = {
  id: string;
  title: string;
  subtitle?: string | null;
  meta?: string | null;
  route: string;
  badge?: string | null;
  tone?: string;
  dueDate?: string | null;
};

export type ModuleDashboardDTO = WithEmptyState & {
  moduleKey: ModuleKey;
  label: string;
  metrics: ModuleMetricDTO[];
  recentItems: ModuleListItemDTO[];
  urgentItems: ModuleListItemDTO[];
  monitorHighlights?: ModuleListItemDTO[];
};

export type ModuleProcessItemDTO = {
  id: string;
  code: string;
  title: string;
  clientName?: string | null;
  holder?: string | null;
  responsible?: string | null;
  status: string;
  phase?: string | null;
  dueDate?: string | null;
  filingDate?: string | null;
  publicationDate?: string | null;
  lastDispatchCode?: string | null;
  lastDispatchText?: string | null;
  lastDispatchDate?: string | null;
  patentId?: string | null;
  publicationNumber?: string | null;
  priority?: number;
  country?: string | null;
  watchActive?: boolean;
  route: string;
  metadata?: Record<string, unknown>;
};

export type ModuleProcessListDTO = WithEmptyState & {
  moduleKey: ModuleKey;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  items: ModuleProcessItemDTO[];
};

export type ModuleMonitorItemDTO = {
  id: string;
  title: string;
  subtitle?: string | null;
  type: string;
  severity: string;
  detail?: string | null;
  route: string;
  dueDate?: string | null;
};

export type ModuleMonitorOverviewDTO = WithEmptyState & {
  moduleKey: ModuleKey;
  metrics: ModuleMetricDTO[];
  items: ModuleMonitorItemDTO[];
};

export type ModuleInternationalItemDTO = {
  id: string;
  title: string;
  subtitle?: string | null;
  detail?: string | null;
  route: string;
};

export type ModuleInternationalDTO = WithEmptyState & {
  moduleKey: ModuleKey;
  metrics: ModuleMetricDTO[];
  items: ModuleInternationalItemDTO[];
};

function getErrorStatus(error: unknown) {
  return typeof error === "object" && error && "response" in error
    ? Number((error as any).response?.status || 0)
    : 0;
}

function createEmptyUiState(moduleKey: ModuleKey, reason: EmptyStateReason): ModuleUiStateDTO {
  return {
    moduleKey,
    sidebarCollapsed: false,
    aiHidden: false,
    updatedAt: null,
    emptyStateReason: reason,
  };
}

function createEmptyDashboard(moduleKey: ModuleKey, reason: EmptyStateReason): ModuleDashboardDTO {
  return {
    moduleKey,
    label: moduleKey,
    metrics: [],
    recentItems: [],
    urgentItems: [],
    monitorHighlights: [],
    emptyStateReason: reason,
  };
}

function createEmptyProcesses(moduleKey: ModuleKey, reason: EmptyStateReason): ModuleProcessListDTO {
  return {
    moduleKey,
    page: 1,
    pageSize: 18,
    total: 0,
    totalPages: 0,
    items: [],
    emptyStateReason: reason,
  };
}

function createEmptyMonitor(moduleKey: ModuleKey, reason: EmptyStateReason): ModuleMonitorOverviewDTO {
  return {
    moduleKey,
    metrics: [],
    items: [],
    emptyStateReason: reason,
  };
}

function createEmptyInternational(moduleKey: ModuleKey, reason: EmptyStateReason): ModuleInternationalDTO {
  return {
    moduleKey,
    metrics: [],
    items: [],
    emptyStateReason: reason,
  };
}

export const workspaceService = {
  getModuleState: async (moduleKey: ModuleKey) => {
    try {
      const { data } = await api.get<ModuleUiStateDTO>("/v2/ui/module-state", {
        params: { moduleKey },
      });
      return data;
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 401 || status === 403) {
        return createEmptyUiState(moduleKey, status === 401 ? "unauthorized" : "forbidden");
      }
      throw error;
    }
  },

  updateModuleState: async (payload: ModuleUiStateDTO) => {
    const { data } = await api.put<ModuleUiStateDTO>("/v2/ui/module-state", payload);
    return data;
  },

  getFavorites: async (moduleKey: ModuleKey) => {
    try {
      const { data } = await api.get<{ moduleKey: ModuleKey; items: ModuleFavoriteDTO[] }>("/v2/ui/favorites", {
        params: { moduleKey },
      });
      return data;
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 401 || status === 403) {
        return { moduleKey, items: [] };
      }
      throw error;
    }
  },

  createFavorite: async (payload: {
    moduleKey: ModuleKey;
    favoriteType: string;
    entityId?: string | null;
    route: string;
    label: string;
    iconKey?: string | null;
    sortOrder?: number;
    metadata?: Record<string, unknown>;
  }) => {
    const { data } = await api.post("/v2/ui/favorites", payload);
    return data;
  },

  deleteFavorite: async (id: string) => {
    const { data } = await api.delete(`/v2/ui/favorites/${id}`);
    return data;
  },

  getDashboard: async (moduleKey: ModuleKey) => {
    try {
      const { data } = await api.get<ModuleDashboardDTO>(`/v2/${moduleKey}/dashboard`);
      return data;
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 401 || status === 403) {
        return createEmptyDashboard(moduleKey, status === 401 ? "unauthorized" : "forbidden");
      }
      throw error;
    }
  },

  getProcesses: async (moduleKey: ModuleKey, params?: { page?: number; pageSize?: number; q?: string; search?: string; status?: string }) => {
    try {
      const { data } = await api.get<ModuleProcessListDTO>(`/v2/${moduleKey}/processos`, {
        params,
      });
      return data;
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 401 || status === 403) {
        return createEmptyProcesses(moduleKey, status === 401 ? "unauthorized" : "forbidden");
      }
      throw error;
    }
  },

  getMonitor: async (moduleKey: ModuleKey) => {
    try {
      const { data } = await api.get<ModuleMonitorOverviewDTO>(`/v2/${moduleKey}/monitor`);
      return data;
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 401 || status === 403) {
        return createEmptyMonitor(moduleKey, status === 401 ? "unauthorized" : "forbidden");
      }
      throw error;
    }
  },

  getInternational: async (moduleKey: ModuleKey) => {
    try {
      const { data } = await api.get<ModuleInternationalDTO>(`/v2/${moduleKey}/internacional`);
      return data;
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 401 || status === 403) {
        return createEmptyInternational(moduleKey, status === 401 ? "unauthorized" : "forbidden");
      }
      throw error;
    }
  },

  getWorkspaceTabs: async () => {
    try {
      const { data } = await api.get<WorkspaceTabsStateDTO | LegacyWorkspaceTabsStateDTO>("/v2/ui/workspace-tabs");
      return normalizeWorkspaceTabsState(data);
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 401 || status === 403 || status === 404) {
        return null;
      }
      throw error;
    }
  },

  saveWorkspaceTabs: async (payload: SaveWorkspaceTabsPayload) => {
    try {
      const { data } = await api.put<WorkspaceTabsStateDTO>("/v2/ui/workspace-tabs", {
        tabsByModule: Object.fromEntries(
          WORKSPACE_MODULE_IDS.map((moduleId) => [
            moduleId,
            (payload.tabsByModule[moduleId] ?? []).map(toWorkspaceTabDTO),
          ]),
        ) as WorkspaceTabsByModuleDTO,
        activeTabIdByModule: payload.activeTabIdByModule,
      });
      return normalizeWorkspaceTabsState(data);
    } catch (error) {
      console.warn("Failed to save workspace tabs remotely", error);
      return null;
    }
  },
};
